/**
 * The Additional Discount block on the customer quotation.
 *
 * Four real fields on the live `Quotation`: `apply_additional_discount_on` (a Select of exactly
 * `Grand Total` and `Net Total`), `additional_discount_percentage`, `additional_discount_amount`,
 * and `base_discount_amount` for the company-currency figure.
 *
 * ---- One figure, two ways of writing it ----
 *
 * "Either enter percentage or Amount." They are not two settings; they are the same discount, and
 * typing either derives the other. `mode` records which the purchaser last touched, so the screen
 * knows which one leads — without it, rounding the derived value back into the source turns 10%
 * into 9.99% the moment anyone looks away.
 *
 * ---- What it refuses to do ----
 *
 * A discount can reduce a total to nothing. It can never invert it: a negative invoice is not a
 * discount, and a half-typed minus sign is not an instruction. Both are treated as no discount, and
 * an over-large amount is capped at the base with `pct: 100` so the caller can see it was capped.
 */

/** Which total the percentage is taken against — the ERP's own two options, verbatim. */
export type DiscountBasis = 'Grand Total' | 'Net Total'

export interface DiscountInput {
  /** Which field the purchaser last typed in. The other is derived from it. */
  mode: 'pct' | 'amount'
  value: number
}

export interface ResolvedDiscount {
  pct: number
  amount: number
  /** The total the percentage was taken against — shown, so the figure can be checked. */
  base: number
}

const round2 = (n: number): number => Math.round(n * 100) / 100

/** A value that is absent, NaN, infinite or negative is not a discount — it is nothing. */
const clean = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0)

export function resolveDiscount(
  input: DiscountInput,
  totals: { netTotal: number; grandTotal: number },
  basis: DiscountBasis,
): ResolvedDiscount {
  const base = round2(basis === 'Net Total' ? totals.netTotal : totals.grandTotal)
  const value = clean(input.value)

  if (input.mode === 'pct') {
    // Capped at 100%: beyond that the amount would exceed the total and invert the invoice.
    const pct = Math.min(value, 100)
    return { pct: round2(pct), amount: round2((base * pct) / 100), base }
  }

  const amount = Math.min(value, base)
  return {
    // A percentage of nothing is not a number. `0` is the honest reading — `Infinity` would render
    // as "Infinity%" next to real money.
    pct: base > 0 ? round2((amount / base) * 100) : 0,
    amount: round2(amount),
    base,
  }
}

/** The total after the discount. Never negative — see the module header. */
export function discountedTotal(total: number, discountAmount: number): number {
  return round2(Math.max(0, total - clean(discountAmount)))
}

/**
 * Which of the two STORED figures leads.
 *
 * ERPNext's own convention (2026-09-18): a stored percentage LEADS and the amount is derived
 * from it against the current base; an amount-led discount is stored with the percentage at 0
 * (`discountCommitPatch`). This replaced a guess — "the % led if it reproduces the amount to
 * the cent" — which any reprice broke: 10% stored with an amount computed against the old net
 * read as "8.52%" once the lines changed, under a header still saying 10%.
 */
export function leadingDiscount(stored: { pct: number; amount: number }): DiscountInput | null {
  const pct = clean(stored.pct)
  const amount = clean(stored.amount)
  if (pct > 0) return { mode: 'pct', value: pct }
  if (amount > 0) return { mode: 'amount', value: amount }
  return null
}

/**
 * What typing either field stores. A percentage: itself and its amount. An amount: itself, with
 * the percentage at 0 — so the amount is what leads on the next read, and "400" is never turned
 * into "399" by a rounded percentage (Akhil, 2026-09-15).
 */
export function discountCommitPatch(
  input: DiscountInput,
  totals: { netTotal: number; grandTotal: number },
  basis: DiscountBasis,
): { additional_discount_percentage: number; additional_discount_amount: number } {
  const next = resolveDiscount(input, totals, basis)
  return {
    additional_discount_percentage: input.mode === 'pct' ? next.pct : 0,
    additional_discount_amount: next.amount,
  }
}

/**
 * What choosing a basis writes: the basis, and BOTH figures cleared (Akhil, 2026-09-17). A 10%
 * set against the grand total is not a 10% against the net — carrying the figures across would
 * silently change the amount the customer gets — so the purchaser types the discount again
 * against the basis they just chose. `base_discount_amount` goes with them; the derived-figures
 * sync would zero it a beat later anyway, but a write that leaves the three consistent is better
 * than one that briefly does not.
 */
export function basisChangePatch(basis: DiscountBasis): {
  apply_discount_on: DiscountBasis
  additional_discount_percentage: 0
  additional_discount_amount: 0
  base_discount_amount: 0
} {
  return { apply_discount_on: basis, additional_discount_percentage: 0, additional_discount_amount: 0, base_discount_amount: 0 }
}
